<template>
  <v-card
    variant="flat"
    :class="{
      'gray-background-popover': isWaitingInitialData(deviceId)
    }"
  >
    <PopoverTitle
      :name="name"
      :deviceType="deviceType"
      :serial="serial"
      :accessLevel="accessLevel"
      tabIndex="0"
    >
      <template v-slot:menu>
        <PopoverMenu
          :deviceId="deviceId"
          :accessLevel="accessLevel"
          :permissions="permissions"
          @unregister="closeInfoWindow"
          data-cy="popoverMenu"
        />
      </template>
      <template v-slot:status>
        <PopoverToolbar :deviceId="deviceId" />
      </template>
    </PopoverTitle>
    <hr />
    <div class="card-content" data-cy="devicePopover">
      <PopoverMainContent
        :deviceId="deviceId"
        :permissions="permissions"
        :isWaitingInitialData="isWaitingInitialData(deviceId)"
      ></PopoverMainContent>
      <v-btn
        variant="outlined"
        class="text-black history-btn"
        @click="openHistoryDialog"
        data-cy="openHistoryDialog"
      >
        {{ $t('map.historyDialog.title') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// Title components
import PopoverTitle from '../content/title/popoverTitle.vue'
import PopoverMenu from '../content/title/popoverMenu.vue'
import PopoverToolbar from '../content/title/popoverToolbar.vue'

// content
import PopoverMainContent from '../content/popoverMainContent.vue'

import { Access } from '@/services/device-access'
import { deviceModelDisplay, deviceSubmodelDisplay } from '@/services/map'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { useWaitingData } from '@/composables/useWaitingData'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { UnpackedDevice } from '@/services/device-service/devices'
import type { GetterByDeviceId, GetterByModel, GetterByModelSubmodel } from '../types'

// This component is used to show the device details including device general information (name, device status, and system status),
// latest telemetry, change over time, and averages.
// getUserDevices function from App.vue updates the Vuex store every 60 seconds. This includes device general information and latest telemetry.
// There's no need to update device details card every 60 seconds, because the request to summary devices updates the store.
// The only data from device details card that isn't updated by summary devices is change over time and averages. So, these
// two calls are required every 60 seconds.
export default defineComponent({
  name: 'DevicePopover',
  setup() {
    const { isWaitingInitialData } = useWaitingData()
    return { isWaitingInitialData }
  },
  emits: {
    close: () => true,
    openHistoryDialog: (_deviceId: string) => true
  },
  props: {
    deviceId: {
      type: String,
      default: ''
    }
  },
  components: {
    PopoverTitle,
    PopoverMenu,
    PopoverMainContent,
    PopoverToolbar
  },
  data() {
    return {
      accountId: null as string | null
    }
  },
  mounted() {
    this.accountId = getUserAccountId(this.$auth)
  },
  watch: {
    allDevices(): void {
      if (this.deviceId) {
        this.checkDeviceAccess()
      }
    }
  },
  computed: {
    allDevices(): Record<string, UnpackedDevice> {
      return this.$store.state.devices.allDevices
    },
    id(): string {
      return this.deviceId || ''
    },
    deviceModel(): string {
      const getModel = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      const getModelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
      ] as GetterByModel<string>

      const model = getModel(this.id)
      let modelKey = ''
      if (model) {
        modelKey = getModelKey(model)
      }
      return deviceModelDisplay(modelKey, this.$t.bind(this))
    },
    deviceSubmodel(): string {
      const getModel = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      const getSubmodel = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      const getSubmodelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY}`
      ] as GetterByModelSubmodel<string>

      const model = getModel(this.id)
      const submodel = getSubmodel(this.id)
      const submodelKey = getSubmodelKey(model, submodel)
      return deviceSubmodelDisplay(submodelKey, this.$t.bind(this))
    },
    deviceType(): string {
      return this.deviceSubmodel ? `${this.deviceModel} (${this.deviceSubmodel})` : this.deviceModel
    },
    serial(): string {
      if (!this.isMine && !this.isShared) {
        return ''
      }
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.id)
    },
    name(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.id)
    },
    isMine(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return this.accountId === getter(this.id)
    },
    isShared(): boolean {
      if (this.isMine) return false
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
      ] as GetterByDeviceId<boolean>
      return getter(this.id)
    },
    accessLevel(): number {
      if (this.isMine) return Access.FULL
      if (this.isShared) return Access.SHARED
      return Access.NONE
    },
    permissions(): string[] {
      return getPermissionsByDeviceId(this.deviceId)
    }
  },
  methods: {
    openHistoryDialog(): void {
      this.$emit('openHistoryDialog', this.id)
    },
    closeInfoWindow(): void {
      this.$emit('close')
    },
    checkDeviceAccess(): void {
      const device = this.allDevices[this.deviceId]
      if (!device) {
        this.closeInfoWindow()
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.card-content {
  overflow: hidden;
  padding: 0 0.5rem 0rem 0.5rem;
}

hr {
  opacity: 0.16;
}

.gray-background-popover {
  background-color: #efefef !important;
  :deep(.aqi) {
    .reading {
      background-color: grey !important;
    }
  }
  :deep(.inactiveTimestamp) {
    color: black !important;
  }
  :deep(.standard-card) {
    height: 52%;
    top: 40%;
  }
}

.history-btn {
  position: absolute;
  right: 16px;
  bottom: 16px;
}
</style>
